import { Link } from 'react-router-dom';
import { line, MAIL_ROUTE, documentAttachmentsPath, documentMailLink, formatDateTime, isDocumentAttachmentKind, t, type DocumentAttachmentsAnswer, type DocumentMailRow } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { useApi } from './api';
import styles from './DocumentPage.module.css';

/**
 * 🔒 The messages tagged to a document, under its uploaded files as From mail
 * (piece 32; decision 1663, T1072): each message's subject opens it in the mail
 * client, beside its sender, its date and its direction, and its attachments are
 * named beneath it, read-only. Tagging copies nothing, so the message and its
 * files stay in the mailbox.
 *
 * 🔨 The rows are only those the server let this viewer read: a reader without
 * the mailbox's read is answered none, and no section is drawn at all.
 */
export function DocumentFromMail({ rows }: { rows: readonly DocumentMailRow[] }) {
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  if (rows.length === 0) return null;
  return (
    <div data-document-from-mail={rows.length}>
      <h3 className={styles.heading}>{t('documentFiles.fromMail')}</h3>
      <DetailList look="separator">
        {rows.map((row) => (
          <DetailRow
            key={row.id}
            data-document-mail={row.id}
            data-document-mail-direction={row.direction}
            title={<Link to={documentMailLink(row, MAIL_ROUTE)} data-document-mail-open={row.id}>{row.subject || t('documentFiles.fromMail.noSubject')}</Link>}
            detail={[
              line(t(row.direction === 'Outbound' ? 'documentFiles.fromMail.sent' : 'documentFiles.fromMail.received'), '·', row.from, '·', formatDateTime(row.at, zone, dateFormat)),
              ...row.attachments.map((attachment) => (
                <span key={attachment.id} data-document-mail-attachment={attachment.id}>
                  {attachment.blocked ? line(attachment.filename, '·', t('documentFiles.fromMail.blocked')) : attachment.filename}
                </span>
              )),
            ]}
          />
        ))}
      </DetailList>
    </div>
  );
}

/**
 * The From mail list for a kind whose page answer carries its own files, read
 * on its own: the Quote, the Sales Order, the Invoice and the Bill. A refused or
 * failed read draws nothing, because the files above it stand either way.
 */
export function DocumentFromMailRead({ recordType, recordId, apiBaseUrl = '' }: { recordType: string; recordId: string; apiBaseUrl?: string }) {
  const read = useApi<DocumentAttachmentsAnswer>(isDocumentAttachmentKind(recordType) ? documentAttachmentsPath(recordType, recordId, false) : null, apiBaseUrl);
  const rows = read.state.kind === 'ready' && Array.isArray(read.state.data?.mail) ? read.state.data.mail : [];
  return <DocumentFromMail rows={rows} />;
}
